import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";

import ChartBlock, { chartHasSeriesData } from "./ChartBlock";
import CombinedRetailerChartBlock from "./CombinedRetailerChartBlock";
import FacetLineChartGrid, { facetChartHasSeriesData } from "./FacetLineChartGrid";
import DesignAccordion from "./design/Accordion";
import { statisticCardContainerSx } from "./constants";
import {
  monthlyByProductChartSubtitle,
  monthlyByProductChartTitle,
  monthlyByProvinceChartSubtitle,
  monthlyByProvinceChartTitle,
  monthlyByRetailerChartSubtitle,
  monthlyByRetailerChartTitle,
  quarterlyChartSubtitle,
  quarterlyChartTitle,
} from "./pricingReportChartCopy";

export default function PricingReportCharts({
  chartDateFrom,
  chartDateTo,
  selectedProduct,
  selectedProductCount = 0,
  monthlyByProductFacetChart,
  monthlyByProductCandlestickChart,
  monthlyByProductCombinedChart,
  productChartCombinedView = false,
  onProductChartCombinedViewChange,
  monthlyByRetailerChart,
  monthlyByRetailerCombinedChart,
  retailerChartCombinedView = false,
  onRetailerChartCombinedViewChange,
  monthlyByProvinceChart,
  monthlyByProvinceCombinedChart,
  provinceChartCombinedView = false,
  onProvinceChartCombinedViewChange,
  quarterlyChart,
  isMonthlyByProductLoading,
  isMonthlyByRetailerLoading,
  isMonthlyByProvinceLoading,
  isQuarterlyLoading,
  monthlyByProductError,
  monthlyByRetailerError,
  monthlyByProvinceError,
  quarterlyError,
  quarterlyLookbackYears,
  onQuarterlyLookbackChange,
  availableQuarterlyLookbackYears = [],
  onMonthlyProductFacetClick,
  accordionExpanded,
  onAccordionExpandedChange,
  onAccordionEntered,
  chartRefMonthlyProduct,
  monthlyByProductChartGranularity = "month",
  chartRefMonthlyRetailer,
  chartRefMonthlyProvince,
  chartRefQuarterly,
}) {
  const hasSelectedProduct = Boolean(selectedProduct?.id) || selectedProductCount > 0;
  const chartDateContext = { dateFrom: chartDateFrom, dateTo: chartDateTo };
  const monthlyProductHasData = productChartCombinedView
    ? chartHasSeriesData(monthlyByProductCombinedChart)
    : hasSelectedProduct
      ? chartHasSeriesData(monthlyByProductCandlestickChart)
      : facetChartHasSeriesData(monthlyByProductFacetChart);
  const monthlyRetailerHasData = retailerChartCombinedView
    ? chartHasSeriesData(monthlyByRetailerCombinedChart)
    : facetChartHasSeriesData(monthlyByRetailerChart);
  const monthlyProvinceHasData = provinceChartCombinedView
    ? chartHasSeriesData(monthlyByProvinceCombinedChart)
    : facetChartHasSeriesData(monthlyByProvinceChart);
  const quarterlyHasData = chartHasSeriesData(quarterlyChart);

  return (
    <DesignAccordion
      title="Pricing charts"
      subtitle="- Track product pricing over time."
      expanded={accordionExpanded}
      onChange={onAccordionExpandedChange}
      onEntered={onAccordionEntered}
    >
      <Stack spacing={2}>
          <Box sx={{ p: 2, ...statisticCardContainerSx }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexWrap="wrap"
              gap={1}
              sx={{ mb: productChartCombinedView || monthlyProductHasData ? 1.5 : 0 }}
            >
              <Box>
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, textTransform: "uppercase" }}>
                  {monthlyByProductChartTitle(monthlyByProductChartGranularity)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {monthlyByProductChartSubtitle(
                    hasSelectedProduct,
                    monthlyByProductChartGranularity,
                    chartDateContext,
                    productChartCombinedView,
                    selectedProductCount
                  )}
                </Typography>
              </Box>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {!hasSelectedProduct ? (
                  <ToggleButton
                    size="small"
                    value="combine"
                    selected={productChartCombinedView}
                    onChange={() => onProductChartCombinedViewChange?.(!productChartCombinedView)}
                  >
                    Combine
                  </ToggleButton>
                ) : null}
              </Stack>
            </Stack>
            {productChartCombinedView ? (
              <CombinedRetailerChartBlock
                chart={monthlyByProductCombinedChart}
                isLoading={isMonthlyByProductLoading}
                error={monthlyByProductError}
                chartRef={monthlyProductHasData ? chartRefMonthlyProduct : undefined}
              />
            ) : hasSelectedProduct ? (
              <ChartBlock
                chart={monthlyByProductCandlestickChart}
                chartType="candlestick"
                isLoading={isMonthlyByProductLoading}
                error={monthlyByProductError}
                framed={false}
                chartRef={monthlyProductHasData ? chartRefMonthlyProduct : undefined}
              />
            ) : (
              <FacetLineChartGrid
                ref={monthlyProductHasData ? chartRefMonthlyProduct : undefined}
                facetChart={monthlyByProductFacetChart}
                isLoading={isMonthlyByProductLoading}
                error={monthlyByProductError}
                framed={false}
                onFacetClick={onMonthlyProductFacetClick}
              />
            )}
          </Box>

          {hasSelectedProduct ? (
            <Box sx={{ p: 2, ...statisticCardContainerSx }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexWrap="wrap"
                gap={1}
                sx={{ mb: retailerChartCombinedView || monthlyRetailerHasData ? 1.5 : 0 }}
              >
                <Box>
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, textTransform: "uppercase" }}>
                    {monthlyByRetailerChartTitle()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {monthlyByRetailerChartSubtitle(retailerChartCombinedView, chartDateContext)}
                  </Typography>
                </Box>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  <ToggleButton
                    size="small"
                    value="combine"
                    selected={retailerChartCombinedView}
                    onChange={() => onRetailerChartCombinedViewChange?.(!retailerChartCombinedView)}
                  >
                    Combine
                  </ToggleButton>
                </Stack>
              </Stack>
              {retailerChartCombinedView ? (
                <CombinedRetailerChartBlock
                  chart={monthlyByRetailerCombinedChart}
                  isLoading={isMonthlyByRetailerLoading}
                  error={monthlyByRetailerError}
                  chartRef={monthlyRetailerHasData ? chartRefMonthlyRetailer : undefined}
                />
              ) : (
                <FacetLineChartGrid
                  ref={monthlyRetailerHasData ? chartRefMonthlyRetailer : undefined}
                  facetChart={monthlyByRetailerChart}
                  isLoading={isMonthlyByRetailerLoading}
                  error={monthlyByRetailerError}
                  framed={false}
                />
              )}
            </Box>
          ) : null}

          {hasSelectedProduct ? (
            <Box sx={{ p: 2, ...statisticCardContainerSx }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexWrap="wrap"
                gap={1}
                sx={{ mb: provinceChartCombinedView || monthlyProvinceHasData ? 1.5 : 0 }}
              >
                <Box>
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, textTransform: "uppercase" }}>
                    {monthlyByProvinceChartTitle()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {monthlyByProvinceChartSubtitle(provinceChartCombinedView, chartDateContext)}
                  </Typography>
                </Box>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  <ToggleButton
                    size="small"
                    value="combine"
                    selected={provinceChartCombinedView}
                    onChange={() => onProvinceChartCombinedViewChange?.(!provinceChartCombinedView)}
                  >
                    Combine
                  </ToggleButton>
                </Stack>
              </Stack>
              {provinceChartCombinedView ? (
                <CombinedRetailerChartBlock
                  chart={monthlyByProvinceCombinedChart}
                  isLoading={isMonthlyByProvinceLoading}
                  error={monthlyByProvinceError}
                  chartRef={monthlyProvinceHasData ? chartRefMonthlyProvince : undefined}
                />
              ) : (
                <FacetLineChartGrid
                  ref={monthlyProvinceHasData ? chartRefMonthlyProvince : undefined}
                  facetChart={monthlyByProvinceChart}
                  isLoading={isMonthlyByProvinceLoading}
                  error={monthlyByProvinceError}
                  framed={false}
                />
              )}
            </Box>
          ) : null}

          {hasSelectedProduct ? (
            <Box sx={{ p: 2, ...statisticCardContainerSx }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexWrap="wrap"
                gap={1}
                sx={{ mb: 1 }}
              >
                <Box>
                  <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, textTransform: "uppercase" }}>
                    {quarterlyChartTitle()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {quarterlyChartSubtitle(quarterlyLookbackYears)}
                  </Typography>
                </Box>
                {availableQuarterlyLookbackYears.length > 1 ? (
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={quarterlyLookbackYears}
                    onChange={(_, next) => {
                      if (!next) return;
                      onQuarterlyLookbackChange(next);
                    }}
                  >
                    {availableQuarterlyLookbackYears.includes(1) ? (
                      <ToggleButton value={1}>1 year</ToggleButton>
                    ) : null}
                    {availableQuarterlyLookbackYears.includes(2) ? (
                      <ToggleButton value={2}>2 years</ToggleButton>
                    ) : null}
                    {availableQuarterlyLookbackYears.includes(5) ? (
                      <ToggleButton value={5}>5 years</ToggleButton>
                    ) : null}
                  </ToggleButtonGroup>
                ) : null}
              </Stack>
              <ChartBlock
                title=""
                chart={quarterlyChart}
                isLoading={isQuarterlyLoading}
                error={quarterlyError}
                framed={false}
                chartType="bar"
                chartRef={quarterlyHasData ? chartRefQuarterly : undefined}
              />
            </Box>
          ) : null}
        </Stack>
    </DesignAccordion>
  );
}

PricingReportCharts.propTypes = {
  chartDateFrom: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object,
  ]),
  chartDateTo: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.object,
  ]),
  selectedProduct: PropTypes.object,
  selectedProductCount: PropTypes.number,
  monthlyByProductFacetChart: PropTypes.object,
  monthlyByProductCandlestickChart: PropTypes.object,
  monthlyByProductCombinedChart: PropTypes.object,
  productChartCombinedView: PropTypes.bool,
  onProductChartCombinedViewChange: PropTypes.func,
  monthlyByRetailerChart: PropTypes.object,
  monthlyByRetailerCombinedChart: PropTypes.object,
  retailerChartCombinedView: PropTypes.bool,
  onRetailerChartCombinedViewChange: PropTypes.func,
  monthlyByProvinceChart: PropTypes.object,
  monthlyByProvinceCombinedChart: PropTypes.object,
  provinceChartCombinedView: PropTypes.bool,
  onProvinceChartCombinedViewChange: PropTypes.func,
  quarterlyChart: PropTypes.object,
  isMonthlyByProductLoading: PropTypes.bool,
  isMonthlyByRetailerLoading: PropTypes.bool,
  isMonthlyByProvinceLoading: PropTypes.bool,
  isQuarterlyLoading: PropTypes.bool,
  monthlyByProductError: PropTypes.string,
  monthlyByRetailerError: PropTypes.string,
  monthlyByProvinceError: PropTypes.string,
  quarterlyError: PropTypes.string,
  quarterlyLookbackYears: PropTypes.number,
  availableQuarterlyLookbackYears: PropTypes.arrayOf(PropTypes.number),
  onQuarterlyLookbackChange: PropTypes.func,
  onMonthlyProductFacetClick: PropTypes.func,
  accordionExpanded: PropTypes.bool,
  onAccordionExpandedChange: PropTypes.func,
  onAccordionEntered: PropTypes.func,
  chartRefMonthlyProduct: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  monthlyByProductChartGranularity: PropTypes.oneOf(["month", "week"]),
  chartRefMonthlyRetailer: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  chartRefMonthlyProvince: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  chartRefQuarterly: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};
